import React, { Component } from 'react';
import numeral from 'numeral';
import CategoryCard from '../CategoryCard/CategoryCard';

import './AttackCategories.scss';

import phoneIconPath from '../../../public/img/phone.png';
import creditCardIconPath from '../../../public/img/credit-card.png';
import teamIconPath from '../../../public/img/team.png';
import employeesIconPath from '../../../public/img/employees.png';
import wifiIconPath from '../../../public/img/wifi.png';
import profileIconPath from '../../../public/img/profile.png';
import loginIconPath from '../../../public/img/login.png';
import unsecuredShieldIconPath from '../../../public/img/unsecured-shield.png';

class AttackCategories extends Component {
  getAllTimeCount(cat, monthly, allTimeCatCount) {
    let count = 0;
    monthly.forEach((mth) => {
      if (mth[cat] > 0) {
        count += mth[cat];
      }
    });
    return Math.max(count, allTimeCatCount);
  }

  formatNum(n) {
    return numeral(n).format('0,0');
  }

  monthlyAvg(monthly, cat) {
    let numMonths = 0;
    let count = 0;
    monthly.forEach((mth) => {
      if (mth[cat] > 0) {
        count += mth[cat];
        numMonths += 1;
      }
    });
    if (numMonths === 0) return 0;
    return this.formatNum(count / numMonths);
  }

  catPercentage(catTotal, catCount) {
    return numeral(catCount / catTotal).format('0.0%');
  }

  render() {
    const allTime = this.props.bundleSummary.attack_cat_all_time;
    const monthly = this.props.bundleSummary.attack_cat_monthly;

    if (!monthly || !allTime) {
      return null;
    }

    const allTimeCorrected = {};
    let catTotal = 0;
    for (const k in allTime) {
      if (!allTime.hasOwnProperty(k)) continue;
      const curCount = this.getAllTimeCount(k, monthly, allTime[k]);
      allTimeCorrected[k] = curCount;
      catTotal += curCount;
    }

    return (
      <div className="categories-container md-grid mobile-fix">
        <h2 className="whos-spying">Who's eavesdropping on your users?</h2>

        <CategoryCard
          id="spyware"
          title="User Device Spyware"
          titleAvatar={phoneIconPath}
          numTotalIncidents={this.formatNum(allTimeCorrected.device_spyware)}
          numMonthlyAvg={this.monthlyAvg(monthly, 'device_spyware')}
          percentage={this.catPercentage(
            catTotal,
            allTimeCorrected.device_spyware,
          )}
          prose="due to spyware on user devices potentially stealing their data through your app"
          dataLeakedA="User payment information"
          iconA={creditCardIconPath}
          complianceA="PCI DSS"
          dataLeakedB="User private details"
          iconB={teamIconPath}
          complianceB="GDPR Information Assurance"
          whyText="Some third-party software installed on your users' devices have the ability to intercept private communication between your app and servers. By tricking users into installing non-official SSL certificates, this software can decrypt encrypted traffic that your app sends out if SSL certificate pinning validation is not enforced."
          riskText="This type of spyware typically claims to be doing &quot;market research&quot; on users, but put themselves in a position to steal the most sensitive information that users are sharing through your app."
          riskScoreMalice={8.0}
          riskScoreExposure={9.5}
          riskScoreBreach={8.0}
        />

        <CategoryCard
          id="employer"
          title="Employer Eavesdropping"
          titleAvatar={employeesIconPath}
          numTotalIncidents={this.formatNum(allTimeCorrected.corporate_appliance)}
          numMonthlyAvg={this.monthlyAvg(monthly, 'corporate_appliance')}
          percentage={this.catPercentage(
            catTotal,
            allTimeCorrected.corporate_appliance,
          )}
          prose="due to employers listening in on their employees' private communications with your service"
          dataLeakedA="All private communications"
          iconA={unsecuredShieldIconPath}
          complianceA="SOC 2"
          dataLeakedB="User payment information"
          iconB={creditCardIconPath}
          complianceB="PCI DSS"
          whyText="Employers often install network proxy devices in the workplace and require employees to install trusted profiles on devices before allowing them on the network. This allows employers to inspect their employees' Internet browsing behavior even if traffic is encrypted."
          riskText="Companies do this to prevent malicious activity within the company network, but at the cost of employees' privacy. Company IT administrators could potentially have access to private data that your users are sharing with you."
          riskScoreMalice={2.0}
          riskScoreExposure={6.0}
          riskScoreBreach={3.0}
        />

        <CategoryCard
          id="wifi"
          title="Insecure Public WiFi"
          titleAvatar={wifiIconPath}
          numTotalIncidents={this.formatNum(allTimeCorrected.captive_portal)}
          numMonthlyAvg={this.monthlyAvg(monthly, 'captive_portal')}
          percentage={this.catPercentage(
            catTotal,
            allTimeCorrected.captive_portal,
          )}
          prose="due to users using your app over public WiFi networks that intercepted their connection"
          dataLeakedA="All private communications"
          iconA={unsecuredShieldIconPath}
          complianceA="SOC 2"
          dataLeakedB="User login credentials"
          iconB={loginIconPath}
          complianceB="GDPR PII protection violation"
          whyText="When users connect to public WiFi networks, they are often faced with &quot;captive portals&quot;, a mechanism to force users to authenticate before granting them access to the network. Captive portals intercept, impersonate, and alter the connection between the client and server, which causes the SSL certificate validation error that we see."
          riskText="Most public WiFi networks at your local coffee shop or mall are not trying to steal your users' private information. However, this same mechanism can easily be exploited by attackers who masquerade as pubic WiFi networks and tap into your users' communication channels with you."
          riskScoreMalice={5.0}
          riskScoreExposure={7.5}
          riskScoreBreach={9.0}
        />

        <CategoryCard
          id="unknown"
          title="Unknown Actors"
          titleAvatar={profileIconPath}
          numTotalIncidents={this.formatNum(allTimeCorrected.unknown)}
          numMonthlyAvg={this.monthlyAvg(monthly, 'unknown')}
          percentage={this.catPercentage(catTotal, allTimeCorrected.unknown)}
          prose="connections intercepted by 3rd parties that we were unable to identify"
          dataLeakedA="All private communications"
          iconA={unsecuredShieldIconPath}
          complianceA="SOC 2"
          whyText="We see a lot of different types of SSL certificate validation failures, but it is impossible to attribute all of them to one of the other categories. This category of attacks represents all the instances of a certificate that you did not authorize attempting to decrypt users' communication channels with you."
          riskText="This type of SSL failure is risky because it is difficult to know the underlying intent of the interceptors."
          riskScoreMalice={9.9}
          riskScoreExposure={5.0}
          riskScoreBreach={5.0}
        />
      </div>
    );
  }
}

export default AttackCategories;



// WEBPACK FOOTER //
// ./src/components/AttackCategories/AttackCategories.jsx